import type { ReactNode } from 'react'
import { useHotkey } from '@tanstack/react-hotkeys'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'

import { useUserFeedbackDialog } from '../hooks/useUserFeedbackDialog'
import { useRegisterLeaveFeedbackCommand } from '../hooks/useRegisterLeaveFeedbackCommand'
import { UserFeedbackDialog } from './UserFeedbackDialog'
import { GlobalHotkeys } from '../../../utils/hotkeys'

interface LeaveFeedbackState {
  isDialogOpen: boolean
}

interface LeaveFeedbackActions {
  closeFeedbackDialog: () => void
  openFeedbackDialog: () => void
}

type LeaveFeedbackContextValue = CompositionContextValue<
  LeaveFeedbackState,
  LeaveFeedbackActions
>

const LeaveFeedbackContext =
  createNonNullableContext<LeaveFeedbackContextValue>('LeaveFeedbackContext')

export const useLeaveFeedbackContext = LeaveFeedbackContext.useRequiredContext

export interface LeaveFeedbackProviderProps {
  readonly children: ReactNode
}

export function LeaveFeedbackProvider({
  children,
}: LeaveFeedbackProviderProps) {
  const feedbackDialog = useUserFeedbackDialog()

  useRegisterLeaveFeedbackCommand({
    onLeaveFeedback: feedbackDialog.openFeedbackDialog,
  })

  useHotkey(GlobalHotkeys.leaveFeedback, () => {
    feedbackDialog.openFeedbackDialog()
  })

  return (
    <LeaveFeedbackContext.Context
      value={{
        actions: {
          closeFeedbackDialog: feedbackDialog.closeFeedbackDialog,
          openFeedbackDialog: feedbackDialog.openFeedbackDialog,
        },
        state: {
          isDialogOpen: feedbackDialog.isDialogOpen,
        },
      }}
    >
      {children}
      <UserFeedbackDialog feedbackDialog={feedbackDialog} />
    </LeaveFeedbackContext.Context>
  )
}
